import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { lazy, Suspense } from "react";
import { usePageTracking } from "@/hooks/useTracking";
import Home from "./pages/Home";

function PageTracker() {
  usePageTracking();
  return null;
}

const Memberships = lazy(() => import("./pages/Memberships"));
const CustomOrders = lazy(() => import("./pages/CustomOrders"));
const Premades = lazy(() => import("./pages/Premades"));
const MyCustomOrders = lazy(() => import("./pages/account/MyCustomOrders"));
const MyPremadeOrders = lazy(() => import("./pages/account/MyPremadeOrders"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Account = lazy(() => import("./pages/Account"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminSubscribers = lazy(() => import("./pages/admin/AdminSubscribers"));
const AdminTiers = lazy(() => import("./pages/admin/AdminTiers"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminCustomOrders = lazy(() => import("./pages/admin/AdminCustomOrders"));
const AdminCustomProducts = lazy(() => import("./pages/admin/AdminCustomProducts"));
const AdminPremadeShop = lazy(() => import("./pages/admin/AdminPremadeShop"));
const AdminPremadeOrders = lazy(() => import("./pages/admin/AdminPremadeOrders"));
const AdminColoringShop = lazy(() => import("./pages/admin/AdminColoringShop"));
const AdminAllOrders = lazy(() => import("./pages/admin/AdminAllOrders"));
const AdminGallery = lazy(() => import("./pages/admin/AdminGallery"));
const AdminSalesDiscounts = lazy(() => import("./pages/admin/AdminSalesDiscounts"));
const ColoringShop = lazy(() => import("./pages/ColoringShop"));
const Gallery = lazy(() => import("./pages/Gallery"));
const MyShopOrders = lazy(() => import("./pages/account/MyShopOrders"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const About = lazy(() => import("./pages/About"));
const DonationDiscount = lazy(() => import("./pages/DonationDiscount"));
const AdminPieceAnalytics = lazy(() => import("./pages/admin/AdminPieceAnalytics"));
const AdminMonthlyInclusions = lazy(() => import("./pages/admin/AdminMonthlyInclusions"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminCustomize = lazy(() => import("./pages/admin/AdminCustomize"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      // Avoid auto-refetching every time the admin tab regains focus —
      // it adds noticeable lag and can interrupt in-progress edits.
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <ScrollToTop />
            <PageTracker />
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="h-8 w-8 rounded-full border-4 border-brand-pink border-t-transparent animate-spin" /></div>}>
              <Routes>
                {/* Public auth routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                
                {/* Public pages */}
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/coloring-shop" element={<ColoringShop />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/about" element={<About />} />
                
                {/* Protected routes - require authentication */}
                <Route path="/" element={<Home />} />
                 <Route path="/memberships" element={<Memberships />} />
                 <Route path="/custom-orders" element={<CustomOrders />} />
                <Route path="/premades" element={<Premades />} />
                <Route path="/account/custom-orders" element={<ProtectedRoute><MyCustomOrders /></ProtectedRoute>} />
                <Route path="/account/custom-orders/:orderId" element={<ProtectedRoute><MyCustomOrders /></ProtectedRoute>} />
                <Route path="/account/premade-orders" element={<ProtectedRoute><MyPremadeOrders /></ProtectedRoute>} />
                <Route path="/account/premade-orders/:orderId" element={<ProtectedRoute><MyPremadeOrders /></ProtectedRoute>} />
                <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                <Route path="/account/shop-orders" element={<ProtectedRoute><MyShopOrders /></ProtectedRoute>} />
                
                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="subscribers" element={<AdminSubscribers />} />
                  <Route path="tiers" element={<AdminTiers />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="custom-orders" element={<AdminCustomOrders />} />
                  <Route path="custom-products" element={<AdminCustomProducts />} />
                  <Route path="premade-shop" element={<AdminPremadeShop />} />
                  <Route path="premade-orders" element={<AdminPremadeOrders />} />
                  <Route path="coloring-shop" element={<AdminColoringShop />} />
                  <Route path="all-orders" element={<AdminAllOrders />} />
                  <Route path="gallery" element={<AdminGallery />} />
                  <Route path="sales-discounts" element={<AdminSalesDiscounts />} />
                  <Route path="piece-analytics" element={<AdminPieceAnalytics />} />
                  <Route path="monthly-inclusions" element={<AdminMonthlyInclusions />} />
                  <Route path="messages" element={<AdminMessages />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="customize" element={<AdminCustomize />} />
                </Route>

                {/* Donation discount page */}
                <Route path="/donation-discount" element={<DonationDiscount />} />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
              </Routes>
            </Suspense>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
